import { useCallback } from 'react';
import { generateReferenceId } from '@/lib/utils';
import {
  Field,
  useFormApi,
  TextInput,
  Button,
  ButtonVariants,
  ButtonSizes,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import styles from './ReferenceIdField.module.scss';

/**
 * Props:
 *   label       the field's own label — contract id, shipment id, …
 *   prefixLabel accessible name for the prefix box
 *   numberLabel accessible name for the number box
 *   onGenerate  (number) => void — omit it and the field writes its own number box
 *   readOnly    a saved record keeps its id: locks the number and Generate
 *   required    defaults to true; a conditionally-required id passes false and owns the check via `rules`
 *   rules       RHF rules for the number box; the slice owns the message
 *   namePrefix  prepended to both form keys, so several rows can each carry an id
 */
export const ReferenceIdField = ({
  label,
  prefixLabel,
  numberLabel,
  onGenerate,
  readOnly = false,
  required = true,
  rules,
  namePrefix = '',
}) => {
  const t = useTranslations();
  const { setValue } = useFormApi();

  // Every caller wrote the same line back into `idNumber`, so the field does it itself.
  const handleGenerate = useCallback(() => {
    const number = generateReferenceId();
    if (onGenerate) onGenerate(number);
    else setValue(`${namePrefix}idNumber`, number);
  }, [onGenerate, setValue, namePrefix]);

  return (
    <div className={styles.field}>
      <Typography
        variant={TypographyVariants.bodyXs}
        weight={TypographyWeights.semibold}
        color={TypographyColors.secondary}
      >
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </Typography>

      <div className={styles.controls}>
        {/* Always read-only: the prefix is the account's, never typed. */}
        <Field name={`${namePrefix}idPrefix`}>
          {({ id, name, value }) => (
            <TextInput
              disabled
              className={styles.prefix}
              id={id}
              name={name}
              aria-label={prefixLabel}
              value={value}
            />
          )}
        </Field>

        <span className={styles.separator} aria-hidden="true">
          –
        </span>

        {/* A saved record keeps its id, so editing drops the format rules with it. */}
        <Field
          name={`${namePrefix}idNumber`}
          required={required && !readOnly}
          rules={readOnly ? undefined : rules}
        >
          {({ id, name, value, onChange, onBlur, status, message }) => (
            <TextInput
              className={styles.number}
              id={id}
              name={name}
              aria-label={numberLabel}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
              disabled={readOnly}
            />
          )}
        </Field>

        <Button
          variant={ButtonVariants.secondary}
          size={ButtonSizes.md}
          onClick={handleGenerate}
          disabled={readOnly}
        >
          {t('FORM_GENERATE')}
        </Button>
      </div>
    </div>
  );
};
